import {
  CaretDownOutlined,
  ColumnHeightOutlined,
  ColumnWidthOutlined,
  DeploymentUnitOutlined,
  DragOutlined,
  FileDoneOutlined,
  FlagOutlined,
  LoadingOutlined,
  MoonFilled,
  SettingOutlined,
  SunFilled,
} from '@ant-design/icons'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { dateFormat } from '@constants/dateFormat'
import { ItemTypes, shifts } from '@constants/orderDispatch'
import { DateSkipper, GraviButton, Horizontal, MaterialIcon, Texto } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { useRouteOptimization } from '@hooks/useRouteOptimization'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Col, Dropdown, Menu, Radio, Tooltip, type TreeDataNode, TreeSelect } from 'antd'
import moment, { type Moment } from 'moment'
import { useDrag } from 'react-dnd'

import ButtonGroup from 'antd/es/button/button-group'
import { useAcceptedOrdersContext } from '../Contexts/AcceptedOrdersContext'
import { useScheduleContext } from '../Contexts/ScheduleContext'
import { ScheduleOrderFilter } from './ScheduleOrderFilter'
import { useMasterMetadata } from '@/api/useMetadata/useMasterMetadata'
import { groupBy, upperCase } from 'lodash'
import { SectorDepotTreeSelect } from '@/components/_SiteWide/SectorDepotTreeSelect'

const store = window.localStorage

type DispatchToolbarProps = {
  openOptimizerWizard?: (val: 'CREATE' | 'REVIEW') => void
  tripTimeDisplay?: string
  setTripTimeDisplay?: (val: string) => void
  config?: Gravitate.ModelConfig
  panelHeights: number[]
  updatePanelHeights?: (val: number[]) => void
  isHorizontalLayout?: boolean
  toggleLayout?: () => void
  isReadonly?: boolean
}

export function DispatchToolbar({
  openOptimizerWizard,
  tripTimeDisplay,
  setTripTimeDisplay,
  config,
  panelHeights,
  updatePanelHeights,
  isHorizontalLayout,
  toggleLayout,
  isReadonly,
}: DispatchToolbarProps) {
  const { scopes } = useSecurityContext()
  const { schedulePayload, setSchedulePayload, toggleShift, selectedDepots, handleDepotChange } = useScheduleContext()
  const { userRunState } = useRouteOptimization()
  const canOptimizeDispatch = scopes?.order_dispatch?.dispatch_optimization
  const { updateSetting } = useUserSettings()
  const dateChange = (date: Moment) => {
    store.setItem('trip-grid-view-date', moment(date).format(dateFormat.YEAR_MONTH_DATE))
    updateSetting('trip-grid-view-date', moment(date).format(dateFormat.YEAR_MONTH_DATE))
    setSchedulePayload((payload: Record<string, unknown>) => ({
      ...payload,
      viewDate: moment(date).format(dateFormat.YEAR_MONTH_DATE),
    }))
  }

  const viewSettingsMenu = (
    <Menu
      onClick={({ key }) => {
        switch (key) {
          case 'tripTimeDisplay-compact':
            updateSetting('dispatch-view-settings::trip-time-display', 'compact')
            setTripTimeDisplay?.('compact')
            break
          case 'tripTimeDisplay-explicit':
            updateSetting('dispatch-view-settings::trip-time-display', 'explicit')
            setTripTimeDisplay?.('explicit')
            break
        }
      }}
      items={[
        {
          type: 'group',
          label: 'Trip Time Display',
          children: [
            {
              label: (
                <Texto
                  appearance={tripTimeDisplay === 'compact' ? 'secondary' : undefined}
                  weight={tripTimeDisplay === 'compact' ? 700 : undefined}
                >
                  Compact
                </Texto>
              ),
              key: 'tripTimeDisplay-compact',
            },
            {
              label: (
                <Texto
                  appearance={tripTimeDisplay === 'explicit' ? 'secondary' : undefined}
                  weight={tripTimeDisplay === 'explicit' ? 700 : undefined}
                >
                  Delivery Window
                </Texto>
              ),
              key: 'tripTimeDisplay-explicit',
            },
          ],
        },
      ]}
    />
  )

  return (
    <Horizontal border='all' gap={8} className='dispatch-toolbar' bg='elevated'>
      <Horizontal gap={16} verticalCenter flex={1}>
        <Texto category='h5' className='grid-count'>
          Driver Dispatch{' '}
        </Texto>
        <SectorDepotTreeSelect onChange={handleDepotChange} value={selectedDepots} />
        <ScheduleOrderFilter />
      </Horizontal>

      <Horizontal fullHeight justifyContent='flex-end' alignItems='center' gap={8} verticalCenter>
        <ButtonGroup>
          {setTripTimeDisplay && (
            <Dropdown overlay={viewSettingsMenu} trigger={['hover']}>
              <GraviButton buttonText={<SettingOutlined style={{ fontSize: '1.25em' }} />} />
            </Dropdown>
          )}

          {toggleLayout && (
            <Tooltip title={`Switch to ${isHorizontalLayout ? 'Vertical' : 'Horizontal'} Layout`}>
              <GraviButton
                onClick={toggleLayout}
                buttonText={
                  isHorizontalLayout ? (
                    <ColumnHeightOutlined style={{ fontSize: '1.25em' }} />
                  ) : (
                    <ColumnWidthOutlined style={{ fontSize: '1.25em' }} />
                  )
                }
              />
            </Tooltip>
          )}

          {!isHorizontalLayout && updatePanelHeights && (
            <Tooltip title={panelHeights[0] === 5 ? 'Collapse' : 'Expand'}>
              <GraviButton
                theme2={panelHeights[0] === 5}
                onClick={() => (panelHeights[0] !== 5 ? updatePanelHeights([5, 95]) : updatePanelHeights([50, 50]))}
                buttonText={
                  <MaterialIcon icon={panelHeights[0] === 5 ? 'bottom_panel_close' : 'bottom_panel_open'} size={20} />
                }
              />
            </Tooltip>
          )}
        </ButtonGroup>

        <Radio.Group
          style={{ display: 'flex', flexWrap: 'nowrap' }}
          buttonStyle='solid'
          value={schedulePayload.shift}
          onChange={(e) => toggleShift(e.target.value)}
        >
          <Tooltip title='Morning'>
            <Radio.Button value={shifts.AM}>
              <SunFilled />{' '}
            </Radio.Button>
          </Tooltip>
          <Tooltip title='Evening'>
            <Radio.Button value={shifts.PM} data-cy='pm_shift_btn'>
              <MoonFilled />
            </Radio.Button>
          </Tooltip>
        </Radio.Group>
        <Horizontal gap={0}>
          <DateSkipper
            daysToSkip={1}
            onChange={dateChange}
            format={formatters.DATE}
            startDate={moment(schedulePayload.viewDate).toString()}
          />
        </Horizontal>

        {!isReadonly && config?.multi_truck && config.auto_trailer_swap && <SpecialItemButton />}

        {!isReadonly && canOptimizeDispatch && openOptimizerWizard && (
          <Dropdown
            overlay={
              <Menu
                onClick={({ key }) => (key === '1' ? openOptimizerWizard('CREATE') : openOptimizerWizard('REVIEW'))}
                items={[
                  {
                    label: 'Run New Optimization',
                    key: '1',
                  },
                  {
                    label: 'View / Manage Runs',
                    key: '2',
                  },
                ]}
              />
            }
            trigger={['hover']}
          >
            <GraviButton
              theme2
              buttonText={
                <Horizontal verticalCenter horizontalCenter>
                  {userRunState?.pendingRun ? (
                    <FileDoneOutlined />
                  ) : userRunState?.hasInProgressRuns ? (
                    <LoadingOutlined spin />
                  ) : (
                    <DeploymentUnitOutlined />
                  )}
                  <Texto style={{ color: 'inherit' }} className=' ml-2 mr-2'>
                    Optimize
                  </Texto>
                  <CaretDownOutlined />
                </Horizontal>
              }
            />
          </Dropdown>
        )}
      </Horizontal>
    </Horizontal>
  )
}

function SpecialItemButton() {
  const { scopes } = useSecurityContext()
  const { setIsDraggingActionBlock } = useAcceptedOrdersContext()
  const canEdit = scopes?.order_dispatch?.edit

  const [, drag] = useDrag({
    item: { type: ItemTypes.TRAILER_SWAP },
    //type: ItemTypes.TRAILER_SWAP,
    collect: (monitor) => {
      setIsDraggingActionBlock(!!monitor.isDragging())
      return {
        isDragging: monitor.isDragging(),
      }
    },
  })

  const dragRef = canEdit ? drag : null

  return (
    <Col className='ml-2'>
      <div ref={dragRef}>
        <GraviButton theme1 appearance='filled' buttonText='Trailer Swap' icon={<DragOutlined />} />
      </div>
    </Col>
  )
}
